/** Clockwise rotation of a local image File/Blob (before upload). */

const ALLOWED_ANGLES = new Set([90, 180, 270]);

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Canvas toBlob failed'));
                    return;
                }
                resolve(blob);
            },
            mime,
            quality,
        );
    });
}

async function loadImageSource(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file, {
                imageOrientation: 'from-image',
            });
        } catch {
            // Fallback below (ältere WebViews)
        }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () =>
                reject(new Error('Bild konnte nicht geladen werden'));
            image.src = url;
        });
        return img;
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Dreht ein Bild um 90/180/270° im Uhrzeigersinn. Dateiname bleibt.
 *
 * @param {File|Blob} file
 * @param {number} [degrees=90]
 * @returns {Promise<File>}
 */
export async function rotateImageFile(file, degrees = 90) {
    if (!file) {
        return file;
    }
    const angle = ((Number(degrees) % 360) + 360) % 360;
    if (!ALLOWED_ANGLES.has(angle)) {
        return file;
    }

    const source = await loadImageSource(file);
    try {
        const srcW = source.width;
        const srcH = source.height;
        if (!srcW || !srcH) {
            throw new Error('Ungültige Bildgröße');
        }
        const swap = angle === 90 || angle === 270;
        const canvas = document.createElement('canvas');
        canvas.width = swap ? srcH : srcW;
        canvas.height = swap ? srcW : srcH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Canvas nicht verfügbar');
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(source, -srcW / 2, -srcH / 2);

        const mime = (file.type || '').includes('png')
            ? 'image/png'
            : 'image/jpeg';
        const blob = await canvasToBlob(
            canvas,
            mime,
            mime === 'image/jpeg' ? 0.92 : undefined,
        );
        const name = file.name || (mime === 'image/png' ? 'image.png' : 'image.jpg');
        return new File([blob], name, {
            type: blob.type || mime,
            lastModified: Date.now(),
        });
    } finally {
        if (typeof source.close === 'function') {
            source.close();
        }
    }
}
